// Deterministic cosmetic noise (architecture §9.3, §14: boil noise is seeded by entity ids, never
// Math.random). Shared by the sketch outline ("line boil") and the hand-drawn placeholders
// (art-assets §4.2: "the same noise as the cell outline"). DOM-free.

import { hash32, hashCombine } from '@ballio/shared';

const TAU = Math.PI * 2;

/** A deterministic value in [0, 1) from three integers. */
export function noise01(a: number, b: number, c: number): number {
  return hashCombine(hashCombine(hash32(a), b), c) / 0x100000000;
}

/** A deterministic value in [−1, 1) from three integers. */
export function noise3(a: number, b: number, c: number): number {
  return noise01(a, b, c) * 2 - 1;
}

/** Harmonics of the closed-curve wobble (angular frequencies) and their base weights. */
const HARMONICS = [2, 3, 5, 8] as const;
const WEIGHTS = [0.4, 0.3, 0.2, 0.1] as const;

/**
 * A smooth, seeded, periodic function of the angle in [−1, 1]: a sum of four harmonics whose
 * phases (and slightly their amplitudes) come from `(seed, frame)`. One `Wobble` per draw call is
 * enough: `seed` it, then sample with `at` or fill a whole vertex ring with `fill`.
 */
export class Wobble {
  private readonly phase = new Float64Array(HARMONICS.length);
  private readonly amp = new Float64Array(HARMONICS.length);

  constructor(seed = 0, frame = 0) {
    this.seed(seed, frame);
  }

  /** Re-seeds for an entity (`seed`) and a boil frame (`frame`). */
  seed(seed: number, frame: number): this {
    let total = 0;
    for (let k = 0; k < HARMONICS.length; k++) {
      this.phase[k] = noise01(seed, frame, k) * TAU;
      const a = (WEIGHTS[k] ?? 0) * (0.6 + 0.4 * noise01(seed, frame, k + 16));
      this.amp[k] = a;
      total += a;
    }
    if (total > 0)
      for (let k = 0; k < HARMONICS.length; k++) this.amp[k] = (this.amp[k] ?? 0) / total;
    return this;
  }

  /** The wobble at angle `theta` (radians), in [−1, 1]. */
  at(theta: number): number {
    let v = 0;
    for (let k = 0; k < HARMONICS.length; k++) {
      v += (this.amp[k] ?? 0) * Math.sin((HARMONICS[k] ?? 0) * theta + (this.phase[k] ?? 0));
    }
    return v;
  }

  /**
   * Writes the wobble at `n` evenly spaced angles `theta0 + i·2π/n` into `out[0..n)`, using
   * rotation recurrences (four multiplies per harmonic and vertex instead of a sine).
   */
  fill(out: Float64Array, n: number, theta0 = 0): void {
    const count = Math.min(n, out.length);
    for (let i = 0; i < count; i++) out[i] = 0;
    for (let k = 0; k < HARMONICS.length; k++) {
      const h = HARMONICS[k] ?? 0;
      const a = this.amp[k] ?? 0;
      const start = h * theta0 + (this.phase[k] ?? 0);
      let s = Math.sin(start);
      let c = Math.cos(start);
      const step = (h * TAU) / n;
      const ds = Math.sin(step);
      const dc = Math.cos(step);
      for (let i = 0; i < count; i++) {
        out[i] = (out[i] ?? 0) + a * s;
        const ns = s * dc + c * ds;
        c = c * dc - s * ds;
        s = ns;
      }
    }
  }
}
